import React, { useEffect, useState } from 'react';
import { newRecordId } from '../../../../api/companyRecords';
import { addClientDocument, listClientDocuments, removeClientDocument } from '../../../../documents/clientDocumentsApi';
import { getCompanyClientDocumentStoragePath } from '../../../auth/utils/storefrontClientSync';
import { deleteStoredFile, objectFileName, objectPathFromUrl, uploadFile } from '../../../../api/files';
import { toJSDate } from '../../../../utils/timestampUtils';
import { formatDistance } from 'date-fns';

const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const MAX_SIZE = 5 * 1024 * 1024;

const displayName = (document) => document?.originalName || document?.name || 'this document';

const StorefrontDocuments = ({ companyId, clientId }) => {
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!companyId || !clientId) return undefined;

    let cancelled = false;
    const loadDocuments = async () => {
      try {
        const next = await listClientDocuments({ companyId, clientId });
        if (!cancelled) setDocuments(next);
      } catch (loadError) {
        console.error('Error loading documents:', loadError);
        if (!cancelled) setError('Could not load documents.');
      }
    };

    loadDocuments();
    return () => {
      cancelled = true;
    };
  }, [companyId, clientId]);

  useEffect(() => {
    if (!success) return undefined;
    const timeout = setTimeout(() => setSuccess(''), 3200);
    return () => clearTimeout(timeout);
  }, [success]);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !companyId || !clientId) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Only PDF, JPEG, and PNG files are allowed.');
      return;
    }
    if (file.size > MAX_SIZE) {
      setError('File size must be less than 5MB.');
      return;
    }

    const uniqueName = objectFileName(file);
    setUploading(true);
    setProgress(0);
    setError('');
    setSuccess('');

    try {
      const objectPath = getCompanyClientDocumentStoragePath(companyId, clientId, uniqueName);
      const stored = await uploadFile(file, objectPath, {
        companyId,
        onProgress: setProgress,
      });
      const document = {
        id: newRecordId(),
        name: uniqueName,
        originalName: file.name,
        type: file.type,
        size: file.size,
        url: stored.url,
        storagePath: objectPath,
        uploadedAt: new Date().toISOString(),
        status: 'pending',
      };
      const next = await addClientDocument({
        companyId,
        clientId,
        document,
      });
      setDocuments(next);
      setSuccess('Document uploaded.');
    } catch (persistError) {
      console.error('Error saving document:', persistError);
      setError('Failed to save document.');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || !companyId || !clientId) return;
    setDeleting(true);
    setError('');
    try {
      const objectPath =
        deleteTarget.storagePath ||
        objectPathFromUrl(deleteTarget.url) ||
        getCompanyClientDocumentStoragePath(companyId, clientId, deleteTarget.name);
      await deleteStoredFile(objectPath, { companyId });
      const next = await removeClientDocument({
        companyId,
        clientId,
        documentId: deleteTarget.id,
      });
      setDocuments(next);
      setDeleteTarget(null);
      setSuccess('Document deleted.');
    } catch (deleteError) {
      console.error('Error deleting document:', deleteError);
      setError('Failed to delete document.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section className="sf-profile__section">
      <h2 className="sf-profile__section-title">Documents</h2>
      <p className="sf-profile__hint">Upload PDFs or images for this business to keep on file.</p>

      <label className="sf-profile-docs__upload">
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={handleUpload}
          disabled={uploading}
        />
        <span className={`sf-profile-btn sf-profile-btn--primary ${uploading ? 'sf-profile-docs__upload-disabled' : ''}`}>
          {uploading ? `Uploading ${Math.round(progress)}%` : 'Upload document'}
        </span>
      </label>

      {uploading ? (
        <div className="sf-profile-docs__progress" aria-hidden="true">
          <span style={{ width: `${Math.max(progress, 4)}%` }} />
        </div>
      ) : null}

      {error ? (
        <p className="sf-profile__banner sf-profile__banner--error" role="alert">{error}</p>
      ) : null}
      {success ? (
        <p className="sf-profile__banner sf-profile__banner--success" role="status">{success}</p>
      ) : null}

      <ul className="sf-profile-docs__list">
        {documents.length === 0 ? (
          <li className="sf-profile-docs__empty">No documents uploaded yet</li>
        ) : documents.map((document) => {
          const uploadedAt = toJSDate(document.uploadedAt);
          return (
            <li key={document.id || document.name} className="sf-profile-docs__item">
              <div className="sf-profile-docs__meta">
                <span className="sf-profile-docs__name">{displayName(document)}</span>
                <span className="sf-profile-docs__when">
                  {uploadedAt
                    ? `Uploaded ${formatDistance(uploadedAt, new Date(), { addSuffix: true })}`
                    : 'Uploaded'}
                </span>
              </div>
              <div className="sf-profile-docs__actions">
                <a
                  className="sf-profile-btn sf-profile-btn--ghost"
                  href={document.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open
                </a>
                <button
                  type="button"
                  className="sf-profile-btn sf-profile-btn--ghost"
                  onClick={() => setDeleteTarget(document)}
                >
                  Delete
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {deleteTarget ? (
        <div className="sf-profile-docs__confirm" role="dialog" aria-labelledby="sf-docs-delete-title">
          <p id="sf-docs-delete-title">Delete “{displayName(deleteTarget)}”? This cannot be undone.</p>
          <div className="sf-profile-docs__actions">
            <button
              type="button"
              className="sf-profile-btn sf-profile-btn--ghost"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="sf-profile-btn sf-profile-btn--primary"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default StorefrontDocuments;
